import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { CreditCard, Landmark, Package, RefreshCw, Loader2, Upload, CheckCircle2, Clock, XCircle, Copy, Printer, ArrowLeft } from 'lucide-react';
import memberApi from '../../lib/memberApi';

const PT_RESIZER = 'https://www.paytr.com/js/iframeResizer.min.js';

const STATUS_MAP = {
  pending: { label: 'Bekliyor', cls: 'bg-secondary text-muted-foreground', icon: Clock },
  waiting_approval: { label: 'Onay Bekliyor', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: Clock },
  paid: { label: 'Ödendi', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2 },
  failed: { label: 'Başarısız', cls: 'bg-destructive/10 text-destructive', icon: XCircle },
  rejected: { label: 'Reddedildi', cls: 'bg-destructive/10 text-destructive', icon: XCircle },
};
const TYPE_LABELS = { one_time: 'Tek Sefer', subscription: 'Abonelik', dues: 'Aidat', donation: 'Bağış' };
const PERIOD_LABELS = { monthly: '/ay', yearly: '/yıl', once: '' };

const backendBase = process.env.REACT_APP_BACKEND_URL || '';

function loadResizer() {
  return new Promise((resolve) => {
    if (window.iFrameResize) return resolve();
    const s = document.createElement('script');
    s.src = PT_RESIZER; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.body.appendChild(s);
  });
}

export default function PaymentsPanel() {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('packages');

  // Flow state
  const [flow, setFlow] = useState(null); // { product, customAmount, method, payment, iframeUrl }
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const iframeRef = useRef(null);

  const loadPayments = useCallback(async () => {
    try { const r = await memberApi.get('/member/payments'); setPayments(r.data); } catch { /* ignore */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        memberApi.get('/payments/config'),
        memberApi.get('/payments/products'),
      ]);
      setConfig(c.data);
      setProducts(p.data);
      await loadPayments();
    } catch {
      toast.error('Ödeme bilgileri yüklenemedi');
    } finally { setLoading(false); }
  }, [loadPayments]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll payment status while a paytr iframe is open
  useEffect(() => {
    if (!flow?.payment || flow.payment.provider !== 'paytr' || !flow.iframeUrl) return;
    const id = setInterval(async () => {
      try {
        const r = await memberApi.get(`/member/payments/${flow.payment.id}`);
        if (r.data.status === 'paid') { clearInterval(id); toast.success('Ödemeniz onaylandı!'); setFlow(null); loadPayments(); setSubTab('history'); }
        else if (r.data.status === 'failed') { clearInterval(id); toast.error('Ödeme başarısız oldu.'); setFlow(null); loadPayments(); }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(id);
  }, [flow, loadPayments]);

  const startFlow = (product) => {
    setFlow({ product, customAmount: '', method: null, payment: null, iframeUrl: null });
  };

  const createPayment = async (provider) => {
    if (!flow) return;
    setBusy(true);
    try {
      const body = { provider, product_id: flow.product?.id || null };
      if (flow.product?.allow_custom_amount && flow.customAmount) body.custom_amount = parseFloat(flow.customAmount);
      const r = await memberApi.post('/member/payments/create', body);
      const payment = r.data;
      if (provider === 'wire') {
        setFlow(f => ({ ...f, method: 'wire', payment, bankAccounts: payment.bank_accounts || config.bank_accounts || [] }));
      } else if (config.paytr_mode === 'direct') {
        // Direct API: build hidden form and submit to PayTR
        const dr = await memberApi.post(`/member/payments/${payment.id}/paytr/direct`);
        submitDirectForm(dr.data.paytr_url, dr.data.fields);
      } else {
        // iFrame
        const ir = await memberApi.post(`/member/payments/${payment.id}/paytr/iframe`);
        await loadResizer();
        setFlow(f => ({ ...f, method: 'paytr', payment, iframeUrl: ir.data.iframe_url }));
        setTimeout(() => { try { if (window.iFrameResize) window.iFrameResize({}, '#paytr-iframe'); } catch { /* noop */ } }, 600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally { setBusy(false); }
  };

  const submitDirectForm = (url, fields) => {
    const form = document.createElement('form');
    form.method = 'POST'; form.action = url;
    Object.entries(fields).forEach(([k, v]) => {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = String(v); form.appendChild(i);
    });
    document.body.appendChild(form); form.submit();
  };

  const uploadReceipt = async (paymentId, file) => {
    if (!file) return;
    setUploadingId(paymentId);
    try {
      const fd = new FormData(); fd.append('file', file);
      await memberApi.post(`/member/payments/${paymentId}/wire/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dekont yüklendi, ödemeniz incelenecek.');
      setFlow(null); await loadPayments(); setSubTab('history');
    } catch (err) { toast.error(err.response?.data?.detail || 'Dekont yüklenemedi'); }
    finally { setUploadingId(null); }
  };

  const copyIban = (iban) => { navigator.clipboard?.writeText(iban); toast.success('IBAN kopyalandı'); };
  const openReceipt = (paymentId) => {
    const token = localStorage.getItem('member_token');
    window.open(`${backendBase}/api/member/payments/${paymentId}/receipt?token=${token}`, '_blank');
  };

  if (loading) {
    return <div className="min-h-[30vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-[hsl(var(--gold))] animate-spin" /></div>;
  }
  if (!config?.payments_enabled) {
    return <div className="p-8 text-center text-sm text-muted-foreground rounded-2xl bg-card border border-border">Ödeme sistemi şu anda aktif değil.</div>;
  }

  // ===== PAYMENT FLOW VIEW =====
  if (flow) {
    const amount = flow.product?.allow_custom_amount && flow.customAmount ? flow.customAmount : flow.product?.price_tl;
    return (
      <div className="space-y-5" data-testid="payment-flow">
        <button onClick={() => setFlow(null)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Geri dön</button>

        <div className="p-5 rounded-2xl bg-card border border-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{flow.product?.name}</div>
              {flow.product?.description && <div className="text-xs text-muted-foreground mt-0.5">{flow.product.description}</div>}
            </div>
            <div className="text-lg font-semibold text-[hsl(var(--gold))]">{amount} {config.currency}</div>
          </div>

          {flow.product?.allow_custom_amount && !flow.payment && (
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1.5">Tutar (TL)</label>
              <input type="number" min="1" step="0.01" value={flow.customAmount} onChange={e => setFlow(f => ({ ...f, customAmount: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" placeholder="Bağış tutarı" data-testid="custom-amount-input" />
            </div>
          )}
        </div>

        {/* Method choose */}
        {!flow.payment && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {config.paytr_enabled && (
              <button onClick={() => createPayment('paytr')} disabled={busy} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-[hsl(var(--gold))] transition-colors text-left disabled:opacity-50" data-testid="method-paytr">
                <CreditCard className="w-6 h-6 text-[hsl(var(--gold))]" />
                <div><div className="font-medium text-sm">Kredi / Banka Kartı</div><div className="text-xs text-muted-foreground">PayTR güvenli ödeme</div></div>
                {busy && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
              </button>
            )}
            {config.bank_transfer_enabled && (
              <button onClick={() => createPayment('wire')} disabled={busy} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-[hsl(var(--gold))] transition-colors text-left disabled:opacity-50" data-testid="method-wire">
                <Landmark className="w-6 h-6 text-[hsl(var(--gold))]" />
                <div><div className="font-medium text-sm">Havale / EFT</div><div className="text-xs text-muted-foreground">Banka hesabına transfer</div></div>
              </button>
            )}
          </div>
        )}

        {/* PayTR iframe */}
        {flow.iframeUrl && (
          <div className="rounded-2xl overflow-hidden border border-border bg-card" data-testid="paytr-iframe-wrap">
            <iframe id="paytr-iframe" ref={iframeRef} src={flow.iframeUrl} title="PayTR Ödeme" frameBorder="0" scrolling="no" style={{ width: '100%', minHeight: 500 }} />
          </div>
        )}

        {/* Wire instructions */}
        {flow.method === 'wire' && flow.payment && (
          <div className="space-y-4" data-testid="wire-instructions">
            <div className="p-4 rounded-xl bg-[hsl(var(--gold)/0.08)] border border-[hsl(var(--gold)/0.3)] text-sm">
              Lütfen aşağıdaki hesaplardan birine <strong>{amount} {config.currency}</strong> tutarında transfer yapın.
              Açıklama kısmına ödeme numaranızı yazın: <code className="px-1.5 py-0.5 rounded bg-card font-mono">{flow.payment.merchant_oid}</code>
            </div>
            {(flow.bankAccounts || []).map(b => (
              <div key={b.id} className="p-4 rounded-xl border border-border bg-card">
                <div className="font-medium text-sm">{b.bank_name}</div>
                {b.account_name && <div className="text-xs text-muted-foreground">{b.account_name}</div>}
                <div className="flex items-center gap-2 mt-1.5">
                  <code className="text-sm font-mono break-all">{b.iban}</code>
                  <button onClick={() => copyIban(b.iban)} className="p-1 rounded hover:bg-secondary shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                {b.description && <div className="text-xs text-muted-foreground mt-1">{b.description}</div>}
              </div>
            ))}
            <div className="p-4 rounded-xl border border-dashed border-border bg-card">
              <div className="text-sm font-medium mb-2">Dekont Yükle (opsiyonel ama önerilir)</div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium">
                {uploadingId === flow.payment.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Dekont Seç
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => uploadReceipt(flow.payment.id, e.target.files[0])} data-testid="wire-receipt-input" />
              </label>
              <p className="text-xs text-muted-foreground mt-2">Dekont yüklediğinizde ödemeniz yönetici onayına gönderilir. “Ödemelerim” bölümünden takip edebilirsiniz.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== MAIN LIST VIEW =====
  return (
    <div className="space-y-5" data-testid="payments-panel">
      <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-secondary">
        <button onClick={() => setSubTab('packages')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${subTab === 'packages' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="subtab-packages">Paketler / Ödeme Yap</button>
        <button onClick={() => setSubTab('history')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${subTab === 'history' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="subtab-history">Ödemelerim</button>
      </div>

      {subTab === 'packages' && (
        products.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground rounded-2xl bg-card border border-border">Şu anda satışta paket bulunmuyor.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="packages-grid">
            {products.map(p => (
              <div key={p.id} className="p-5 rounded-2xl bg-card border border-border flex flex-col" data-testid="package-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                    {p.product_type === 'subscription' ? <RefreshCw className="w-5 h-5 text-[hsl(var(--gold))]" /> : <Package className="w-5 h-5 text-[hsl(var(--gold))]" />}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{TYPE_LABELS[p.product_type] || p.product_type}</span>
                </div>
                <h3 className="font-medium mt-3">{p.name}</h3>
                {p.description && <p className="text-xs text-muted-foreground mt-1 flex-1">{p.description}</p>}
                <div className="flex items-end justify-between mt-4">
                  <div className="text-xl font-semibold text-[hsl(var(--gold))]">
                    {p.allow_custom_amount ? 'Serbest' : `${p.price_tl} ${p.currency}`}
                    {p.product_type === 'subscription' && <span className="text-xs text-muted-foreground font-normal">{PERIOD_LABELS[p.billing_period]}</span>}
                  </div>
                  <button onClick={() => startFlow(p)} className="px-4 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]" data-testid="package-pay-btn">Öde</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {subTab === 'history' && (
        payments.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground rounded-2xl bg-card border border-border">Henüz ödemeniz bulunmuyor.</div>
        ) : (
          <div className="space-y-3" data-testid="payment-history">
            {payments.map(p => {
              const st = STATUS_MAP[p.status] || { label: p.status, cls: 'bg-secondary', icon: Clock };
              const StIcon = st.icon;
              return (
                <div key={p.id} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border" data-testid="payment-history-item">
                  <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                    {p.provider === 'wire' ? <Landmark className="w-4.5 h-4.5 text-[hsl(var(--gold))]" /> : <CreditCard className="w-4.5 h-4.5 text-[hsl(var(--gold))]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{p.description || p.product_name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">#{p.merchant_oid}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-[hsl(var(--gold))]">{p.amount_tl} {p.currency}</div>
                    <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${st.cls}`}>
                      <StIcon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                  {p.status === 'paid' && (
                    <button onClick={() => openReceipt(p.id)} className="p-2 rounded-lg hover:bg-secondary shrink-0" title="Makbuz" data-testid="receipt-btn"><Printer className="w-4 h-4" /></button>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
